/**
 * What: Validates the credit-based run receipt returned by a Shopify run request.
 * Transport / data-flow: Converts an untyped JSON API response into the credit
 * balance DTO consumed by the recovery UI, independent of the response envelope.
 *
 */
import { shopifyCreditBalanceSchema, type ShopifyCreditBalance } from '@testfeed/shopify-contracts'

export type CreditSpendQuote = {
  balance: Extract<ShopifyCreditBalance, { state: 'configured' }>
  quotedCredits: number
}

function readQuotedCredits(value: Record<string, unknown>): number | null {
  const quoted = (value as { quotedCredits?: unknown }).quotedCredits
  return typeof quoted === 'number' && Number.isInteger(quoted) && quoted >= 0 ? quoted : null
}

/**
 * Returns recovery data when the response carries an exhausted credit balance.
 * The server's `USAGE_CREDITS_EXHAUSTED` code is authoritative: the balance snapshot on the
 * response is for display only, and re-deriving the verdict from `availableCredits >= quotedCredits`
 * here can disagree with the server (e.g. billing-account-scoped ledger reads used for the hold
 * enforcing a wider scope than the org-scoped balance read used for display), silently swallowing
 * a real rejection.
 */
export function readCreditExhaustedPrompt(value: unknown): CreditSpendQuote | null {
  if (!value || typeof value !== 'object') return null
  const record = value as { code?: unknown; creditBalance?: unknown }
  if (record.code !== 'USAGE_CREDITS_EXHAUSTED') return null
  const result = shopifyCreditBalanceSchema.safeParse(record.creditBalance)
  if (!result.success || result.data.state !== 'configured') return null
  const quotedCredits = readQuotedCredits(record)
  if (quotedCredits === null) return null
  return { balance: result.data, quotedCredits }
}

/** Returns the quoted spend when the response carries a usable credit balance. */
export function readCreditSpendQuote(value: unknown): CreditSpendQuote | null {
  if (!value || typeof value !== 'object') return null
  const record = value as { creditBalance?: unknown }
  const result = shopifyCreditBalanceSchema.safeParse(record.creditBalance)
  if (!result.success || result.data.state !== 'configured') return null
  const quotedCredits = readQuotedCredits(record)
  if (quotedCredits === null) return null
  return { balance: result.data, quotedCredits }
}
